import React, { useEffect, useState } from "react";
import { Phone, Plus, Trash2, Loader2 } from "lucide-react";
import { phonenumbersAPI } from "../services/api";
import { formatPhoneDisplay } from "../lib/phoneUtils";

/**
 * Phone Numbers: list and manage the user's numbers.
 * Future: this page will show buyable phone numbers for users to purchase (marketplace/catalog).
 */
export default function PhoneNumbers() {
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [addNumber, setAddNumber] = useState("");
  const [addFriendlyName, setAddFriendlyName] = useState("");
  const [addTwilioSid, setAddTwilioSid] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchNumbers();
  }, []);

  async function fetchNumbers() {
    setLoading(true);
    setError(null);
    try {
      const res = await phonenumbersAPI.list();
      const raw = res.data?.numbers;
      if (res.data?.success && Array.isArray(raw)) {
        setNumbers(
          raw.map((n) => ({
            ...n,
            _id: n._id != null ? (typeof n._id === "object" && n._id?.$oid ? n._id.$oid : String(n._id)) : n._id,
          }))
        );
      } else {
        setNumbers([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load phone numbers");
      setNumbers([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd(e) {
    e.preventDefault();
    const raw = (addNumber || "").trim();
    if (!raw) return;
    setSaving(true);
    try {
      await phonenumbersAPI.create({
        number: raw,
        friendlyName: (addFriendlyName || "").trim() || undefined,
        twilioSid: (addTwilioSid || "").trim() || undefined,
      });
      setAddModal(false);
      setAddNumber("");
      setAddFriendlyName("");
      setAddTwilioSid("");
      fetchNumbers();
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Failed to add number");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!id || !window.confirm("Remove this phone number from your account?")) return;
    setDeleteId(id);
    try {
      await phonenumbersAPI.delete(id);
      fetchNumbers();
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Failed to delete");
    } finally {
      setDeleteId(null);
    }
  }

  const isShared = (n) => n.ownerId == null || n.ownerId === undefined;

  return (
    <div className="p-6 bg-slate-50 min-h-full">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-100 text-teal-700">
              <Phone className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Phone Numbers</h1>
              <p className="text-slate-600 text-sm">Your numbers. Configure inbound and outbound on the Inbound and Outbound pages. In the future you’ll be able to purchase new numbers here.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700"
          >
            <Plus className="w-4 h-4" /> Add number
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-800 text-sm">
            {error}
          </div>
        )}

        <div className="mb-4 p-3 rounded-lg bg-slate-100 border border-slate-200 text-slate-600 text-sm">
          <span className="font-medium text-slate-700">Coming soon:</span> Buy phone numbers directly from this page. For now, add a number you already own (e.g. from Twilio) or use shared test numbers on Inbound/Outbound.
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : numbers.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
            <Phone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No phone numbers yet</p>
            <p className="text-slate-500 text-sm mt-1">Add a number you own (e.g. from Twilio). Configure inbound and outbound scripts on the Inbound and Outbound pages.</p>
            <button
              type="button"
              onClick={() => setAddModal(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700"
            >
              <Plus className="w-4 h-4" /> Add number
            </button>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/80">
                  <th className="text-left py-3 px-4 text-slate-600 font-semibold">Number</th>
                  <th className="text-left py-3 px-4 text-slate-600 font-semibold">Name</th>
                  <th className="text-right py-3 px-4 text-slate-600 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {numbers.map((n) => {
                  const id = n._id != null ? String(n._id) : n.number;
                  const shared = isShared(n);
                  return (
                    <tr key={id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="py-3 px-4 text-slate-800">
                        <span className="font-mono">{formatPhoneDisplay(n.number || n.numberNormalized || "") || n.number || "—"}</span>
                      </td>
                      <td className="py-3 px-4 text-slate-600">
                        {n.friendlyName ? String(n.friendlyName).trim() : "—"}
                        {shared && (
                          <span className="ml-2 text-[10px] font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                            Shared
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {!shared && (
                          <button
                            type="button"
                            onClick={() => handleDelete(id)}
                            disabled={deleteId === id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                          >
                            {deleteId === id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {addModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => !saving && setAddModal(false)}>
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Register a phone number</h2>
              <form onSubmit={handleAdd}>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone number *</label>
                <input
                  type="text"
                  value={addNumber}
                  onChange={(e) => setAddNumber(e.target.value)}
                  placeholder="+18055551234 or 8055551234"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg mb-4"
                  required
                />
                <label className="block text-sm font-medium text-slate-700 mb-1">Friendly name (optional)</label>
                <input
                  type="text"
                  value={addFriendlyName}
                  onChange={(e) => setAddFriendlyName(e.target.value)}
                  placeholder="e.g. Main line"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg mb-4"
                />
                <label className="block text-sm font-medium text-slate-700 mb-1">Twilio SID (optional)</label>
                <input
                  type="text"
                  value={addTwilioSid}
                  onChange={(e) => setAddTwilioSid(e.target.value)}
                  placeholder="PN..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg mb-6"
                />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => !saving && setAddModal(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-2">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Add
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
